import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Eye, User, Sparkles, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function SharedImagePage() {
  const { shareId } = useParams();
  const [imageData, setImageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSharedImage();
  }, [shareId]);

  const fetchSharedImage = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/shared/${shareId}`);
      setImageData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "الصورة غير موجودة");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!imageData?.image_base64) return;
    
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${imageData.image_base64}`;
    link.download = `helcoo-shared-${shareId}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("تم تحميل الصورة");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <Sparkles className="w-12 h-12 text-foreground" />
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-6 p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">الصورة غير متاحة</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
        <Link
          to="/"
          className="flex items-center gap-2 px-6 py-3 rounded-full bg-foreground text-background font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          العودة للرئيسية
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-xl border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-foreground flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-background" />
              </div>
              <span className="text-xl font-bold text-foreground">HELCOO</span>
            </Link>
            
            <div className="flex items-center gap-2 text-muted-foreground">
              <Eye className="w-4 h-4" />
              <span className="text-sm">{imageData?.views || 0} مشاهدة</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Image */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl overflow-hidden border border-border shadow-2xl"
        >
          <img
            src={`data:image/png;base64,${imageData?.image_base64}`}
            alt={imageData?.prompt}
            className="w-full"
          />
        </motion.div>

        {/* Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mt-6 p-6 rounded-2xl bg-muted/50 border border-border"
        >
          {/* Creator */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-foreground/10 flex items-center justify-center">
              <User className="w-5 h-5 text-foreground" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">تم الإنشاء بواسطة</p>
              <p className="font-medium text-foreground">{imageData?.creator_name}</p>
            </div>
          </div>

          {/* Prompt */}
          {imageData?.prompt && (
            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-1">الوصف</p>
              <p className="text-foreground">{imageData.prompt}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleDownload}
              className="flex-1 py-3 rounded-xl bg-foreground text-background font-medium flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              تحميل الصورة
            </motion.button>
            <Link
              to="/"
              className="px-6 py-3 rounded-xl bg-muted border border-border text-foreground font-medium flex items-center justify-center gap-2 hover:bg-muted/80"
            >
              <Sparkles className="w-4 h-4" />
              جرب HELCOO
            </Link>
          </div>
        </motion.div>

        {/* Footer */}
        <p className="text-center text-muted-foreground text-sm mt-8">
          تم إنشاء هذه الصورة باستخدام HELCOO
        </p>
      </main>
    </div>
  );
}
